/**
 * MfaEnrollFlow — enrollment TOTP per staff.
 *
 * Step:
 * 1. multiFactor(user).getSession() → TotpMultiFactorGenerator.generateSecret()
 * 2. Genera QR code + segreto in chiaro (per chi non può scansionare)
 * 3. Utente inserisce codice OTP a 6 cifre
 * 4. TotpMultiFactorGenerator.assertionForEnrollment(secret, otp)
 * 5. multiFactor.enroll(assertion, 'TOTP')
 * 6. Cloud Function markMfaEnrolled (audit log + flag /users/{uid})
 * 7. refreshToken + redirect /app
 *
 * Vincoli:
 * - Il segreto è VALIDO solo per ~10 minuti: se l'utente impiega di più,
 *   la verifica fallirà con un errore generico e proponiamo "Rigenera codice".
 * - Il segreto in chiaro non viene mai loggato.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  multiFactor,
  TotpMultiFactorGenerator,
  type TotpSecret,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'sonner';
import { auth, functions } from '../../security/firebase';
import { useAuth } from '../../hooks/useAuth';

export function MfaEnrollFlow() {
  const navigate = useNavigate();
  const { refreshToken } = useAuth();

  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setError(null);
    setGenerating(true);
    try {
      const u = auth.currentUser;
      if (!u || !u.email) throw new Error('not-authenticated');
      const session = await multiFactor(u).getSession();
      const sec = await TotpMultiFactorGenerator.generateSecret(session);
      const url = sec.generateQrCodeUrl(u.email, 'DietaGest');
      setSecret(sec);
      setQrUrl(url);
    } catch {
      setError(
        'Impossibile generare il codice di sicurezza. Esegui logout e riprova.'
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    // Reset secret + setState batch al mount sono intenzionali: la
    // generazione TOTP è il vero side-effect che vogliamo eseguire.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void generate();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secret || otp.length !== 6) {
      setError('Inserisci tutte e 6 le cifre.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const u = auth.currentUser;
      if (!u) throw new Error('not-authenticated');
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        otp
      );
      await multiFactor(u).enroll(assertion, 'TOTP DietaGest');
      const fn = httpsCallable<unknown, { ok: true }>(
        functions,
        'markMfaEnrolled'
      );
      await fn({});
      await refreshToken();
      toast.success('MFA attivata correttamente.');
      navigate('/app', { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/invalid-verification-code') {
        setError('Codice non corretto. Verifica e riprova.');
      } else if (code === 'auth/code-expired') {
        setError('Codice scaduto. Genera un nuovo segreto.');
      } else {
        setError('Attivazione MFA non riuscita. Riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret.secretKey);
      toast.success('Codice copiato negli appunti.');
    } catch {
      toast.error('Impossibile copiare. Trascrivi il codice manualmente.');
    }
  };

  return (
    <div className="min-h-screen bg-bg-app flex items-center justify-center p-6">
      <div className="w-full max-w-[560px] bg-white rounded-xl shadow-sm p-8 md:p-10">
        <header className="mb-8">
          <h1 className="font-headline-lg text-headline-lg text-text-main mb-2">
            Attiva l'autenticazione a due fattori
          </h1>
          <p className="font-body-md text-text-secondary">
            Scansiona il QR con la tua app di autenticazione (Google
            Authenticator, 1Password, Authy…) e inserisci il codice generato.
          </p>
        </header>

        {generating && (
          <p className="text-body-md text-text-secondary mb-4">
            Generazione segreto in corso…
          </p>
        )}

        {qrUrl && secret && (
          <div className="space-y-6">
            <div className="flex items-center justify-center bg-bg-app rounded-lg p-6">
              <QRCodeSVG value={qrUrl} size={200} level="M" />
            </div>

            <div>
              <p className="font-label-sm text-on-surface-variant uppercase mb-1">
                Codice manuale
              </p>
              <div className="flex items-center gap-3">
                <code className="flex-1 font-data-tabular text-body-md bg-bg-app rounded-lg px-4 py-3 break-all">
                  {secret.secretKey}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  aria-label="Copia il codice manuale"
                  className="h-12 px-4 rounded-lg border border-border-gray font-body-md font-semibold hover:bg-surface-container-low"
                >
                  Copia
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <label
                htmlFor="enroll-otp"
                className="block font-label-sm text-on-surface-variant"
              >
                Codice generato dall'app (6 cifre)
              </label>
              <input
                id="enroll-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                aria-invalid={error ? 'true' : 'false'}
                className="w-full h-14 px-4 text-center tracking-[0.5em] text-headline-md font-headline-md rounded-lg border border-border-gray focus:outline-none focus:border-primary focus:ring-2 focus:ring-magenta-soft"
              />
              {error && (
                <p role="alert" className="text-body-sm text-error">
                  {error}
                </p>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={generate}
                  disabled={generating}
                  className="h-12 px-4 rounded-lg border border-border-gray font-body-md font-semibold hover:bg-surface-container-low disabled:opacity-50"
                >
                  Genera nuovo codice
                </button>
                <button
                  type="submit"
                  disabled={submitting || otp.length !== 6}
                  className="flex-1 h-12 rounded-lg bg-primary text-on-primary font-body-md font-bold hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? 'Verifica…' : 'Attiva MFA'}
                </button>
              </div>
            </form>
          </div>
        )}

        {!generating && !qrUrl && error && (
          <div role="alert" className="text-body-md text-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default MfaEnrollFlow;
